import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { generateServiceSchema } from "@/data/serviceSchemaData";
import { Header } from "@/components/Header";
import { PackageCallout } from "@/components/PackageCallout";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { YouTubeShortsSection } from "@/components/YouTubeShortsSection";

// Images
import heroImage from "@/assets/walkways/paver-walkway-steps-installation-north-caldwell-nj-2.webp";
import packanackLakeImage from "@/assets/walkways/paver-walkway-installation-packanack-lake-nj.webp";
import wayneImage from "@/assets/walkways/paver-walkway-installation-wayne-nj.webp";
import pineBrookImage from "@/assets/walkways/paver-walkway-installation-pine-brook-nj.webp";
import totowaImage from "@/assets/walkways/paver-walkway-repair-totowa-nj.webp";
import palisadesParkImage from "@/assets/walkways/paver-walkway-install-palisades-park-bergen-county-nj.webp";

// Citation link component - wraps text with citation number
const CitationLink = ({ num, url, children }: { num: number; url: string; children?: React.ReactNode }) => (
  <a 
    href={url} 
    target="_blank" 
    rel="noopener noreferrer" 
    className="text-secondary hover:underline"
  >
    {children}<sup>{num}</sup>
  </a>
);

const faqs = [
  {
    question: "How long does paver walkway installation take in North Jersey?",
    answer: "Most walkways take 1 to 3 days to complete. Longer or more detailed designs can take a bit more time. Weather can also play a role, especially if rain moves through Bergen or Essex counties during the project.",
  },
  {
    question: "Do I need a permit for a paver walkway in North Jersey?",
    answer: "It depends on your municipality. Some towns require a land-use application; others don't. Setback requirements can vary, too. We recommend checking with your local building department before the work begins, and we can help point you in the right direction.",
  },
  {
    question: "What is the best paver material for North Jersey weather?",
    answer: "Concrete pavers rated for 50 or more freeze-thaw cycles hold up best in our climate. They handle harsh winters without cracking the way poured concrete can. Homeowners in Morris and Passaic counties have seen great results with these materials.",
  },
  {
    question: "Can you replace my concrete sidewalk with pavers?",
    answer: "Yes. We remove the old concrete, properly prep the base, and install pavers in its place. It's a popular upgrade for homeowners who want a cleaner look or are tired of dealing with cracked concrete.",
  },
  {
    question: "Is 2 feet wide enough for a walkway?",
    answer: "Two feet can work for a simple side path, but most front walkways should be wider. We recommend at least 3 to 4 feet so two people can walk side by side comfortably. We talk through width options before starting, so the walkway fits your property and how you plan to use it.",
  },
  {
    question: "How do I maintain my paver walkway after installation?",
    answer: "Periodic cleaning and topping off the joint sand as needed will keep things in good shape. Resealing every 7 to 15 years helps protect the surface, depending on wear and sun exposure. A pressure wash in the spring removes dirt and debris that builds up over winter.",
  },
  {
    question: "Should I hire an ICPI-certified contractor for my North Jersey walkway?",
    answer: "ICPI certification means the contractor has been trained on proper base preparation, drainage, and installation techniques. It's one way to know the crew understands how to build for our local soil and weather conditions.",
  },
];

const citations = [
  {
    num: 1,
    text: "Interlocking Concrete Pavement Institute (ICPI), Tech Spec 2: Construction of Interlocking Concrete Pavement – Base preparation standards for walkways.",
    url: "https://www.icpi.org",
  },
  {
    num: 2,
    text: "ASTM International, ASTM C936: Standard Specification for Solid Concrete Interlocking Paving Units – Paver compressive strength requirements (8,000 PSI minimum).",
    url: "https://www.astm.org",
  },
  {
    num: 3,
    text: "New Jersey Uniform Construction Code, Residential Code Table R301.2 – Frost line depth for Northern New Jersey.",
    url: "https://www.nj.gov/dca/divisions/codes",
  },
  {
    num: 4,
    text: "National Association of Realtors, 2023 Remodeling Impact Report – Outdoor hardscaping features and buyer interest.",
    url: "https://www.nar.realtor",
  },
  {
    num: 5,
    text: "ASCE/ICPI, Standard 58-16: Structural Design of Interlocking Concrete Pavement – Structural design standards for interlocking concrete pavement systems.",
    url: "https://www.icpi.org",
  },
];

const youtubeShortIds = ["p_RCDyOyCgU", "TspgYEFQ_sQ", "Po36ZvNh7ro"];

export default function PaverWalkways() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Paver Walkway Installation Totowa, NJ | Masonry Pro</title>
        <meta name="description" content="Paver walkways with proper grading so water drains and nothing shifts underfoot. Front walks, garden paths, and pool surrounds across North Jersey homes." />
        <meta property="og:description" content="Paver walkways with proper grading so water drains and nothing shifts underfoot. Front walks, garden paths, and pool surrounds across North Jersey homes." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer,
              },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(generateServiceSchema({
            name: "Paver Walkway Installation",
            description: "Professional paver walkway installation in North Jersey with proper drainage and ICPI-standard base work.",
            url: "/paving-services/paver-walkways",
            image: "https://masonry-pro.com/og/paver-walkways.jpg"
          }))}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/paver-walkways.jpg" />
        <meta property="og:title" content="Paver Walkway Installation Totowa, NJ | Masonry Pro" />
        <meta name="twitter:title" content="Paver Walkway Installation Totowa, NJ | Masonry Pro" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/paver-walkways.jpg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section - Centered with lighter overlay */}
          <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
            <img src={heroImage} alt="Paver walkway installation in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(13,16,23,0.45), rgba(13,16,23,0.65))' }} />
            <div className="container mx-auto px-4 relative z-10">
              <div className="max-w-4xl mx-auto text-center">
                <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
                  Local Paver Walkway Installation in <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Totowa, NJ</span> Serving North Jersey
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.25rem', maxWidth: '48rem', margin: '0 auto 1.5rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                  Level, slip-resistant walkways that stand up to our freeze-thaw seasons
                </p>
                <Button variant="gold" size="lg" asChild>
                  <a href="tel:+19737252395">Schedule Your Free Assessment</a>
                </Button>
              </div>
            </div>
          </section>

          <PackageCallout packageId="curb-appeal" packageName="Curb Appeal Package" blurb={"Bundle the walkway with new masonry steps, a stoop, and stone veneer accents so the whole front entry reads as one finished project."} />

          {/* Intro Section with Citation */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto prose prose-lg">
                <p className="text-lg text-muted-foreground">
                  A paver walkway does more than connect your driveway to your front door. It sets the tone for your entire property. We install custom paver walkways for homeowners across <Link to="/service-areas/passaic-county" className="text-secondary hover:underline">Passaic</Link>, <Link to="/service-areas/bergen-county" className="text-secondary hover:underline">Bergen</Link>, <Link to="/service-areas/essex-county" className="text-secondary hover:underline">Essex</Link>, and <Link to="/service-areas/morris-county" className="text-secondary hover:underline">Morris</Link> counties. Our crews follow <CitationLink num={1} url="https://www.icpi.org">ICPI standards</CitationLink> on every project. Whether you're replacing a cracked concrete path or starting fresh, we build walkways that hold up for years.
                </p>
                <p className="text-lg text-muted-foreground mt-4">
                  Walkways fail differently than patios do, since they are narrow and people walk the same line every day. A single paver that heaves half an inch becomes a toe-catcher, and around Totowa and Little Falls we see that happen most often where a walk was laid over topsoil instead of a compacted base. Freeze-thaw does the rest over a couple of winters. We excavate deeper than most crews would for a walkway, and we edge it properly, so the path stays flat and the joints hold. Watch a <Link to="/videos/paver-walkway-steps-north-caldwell-nj" className="text-secondary hover:underline">paver walkway and front steps job in North Caldwell</Link>.
                </p>
              </div>
            </div>
          </section>

          {/* Base + Ground Prep - 2 Column */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12">
                {/* A Proper Base */}
                <div>
                  <div className="aspect-[4/3] rounded-xl overflow-hidden mb-6">
                    <img 
                      src={packanackLakeImage} 
                      alt="Paver walkway installation in Packanack Lake, NJ showing proper gravel base preparation" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    A Proper Base Keeps Your Walkway <span className="text-gradient-gold">Level for Years</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      We use crushed gravel as the foundation for every walkway. When it gets compacted, the pieces lock together. That's what keeps your pavers from shifting or sinking.
                    </p>
                    <p>
                      Some people ask if they can lay pavers on dirt. Technically, yes, but we don't suggest it. Dirt holds moisture and can move around, especially through our North Jersey seasons. A walkway built on bare soil won't stay level for very long.
                    </p>
                    <p>
                      We've gotten calls from homeowners in Montclair and Ridgewood who need their walkways redone. The pavers looked great at first. Then they started sinking a year or two later. No base underneath. That's what can happen.
                    </p>
                    <p>
                      Clay soil shows up all over Essex and Bergen counties. We put down geotextile fabric to keep that clay from mixing into the gravel over time. <CitationLink num={1} url="https://www.icpi.org">ICPI has guidelines on how deep the base should be</CitationLink>, depending on the soil and the level of foot traffic the walkway receives. We stick to those standards.
                    </p>
                  </div>
                </div>

                {/* Ground Preparation */}
                <div>
                  <div className="aspect-[4/3] rounded-xl overflow-hidden mb-6">
                    <img 
                      src={wayneImage} 
                      alt="Paver walkway installation in Wayne, NJ showing ground preparation and excavation" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold mb-6">
                    Ground Preparation Sets the Foundation for a <span className="text-gradient-gold">Lasting Walkway</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Digging out the path is where a walkway really starts, and how deep we go depends on what the soil is doing and where the water needs to end up. On a front entry that runs alongside the foundation, that can mean going deeper than you might expect, because a path that channels runoff back toward the house creates a problem you will be dealing with every spring.
                    </p>
                    <p>
                      We slope the ground slightly to allow water to run off the surface. Even a small pitch makes a big difference. Water that sits on pavers can freeze in winter and cause problems over time.
                    </p>
                    <p>
                      The gravel goes down in layers rather than all at once, and each one gets tamped before the next is placed, which is slower but it is the step that keeps a walkway from settling into dips a few years in. Homeowners in Morristown and Verona have asked us why that adds a day, and the honest answer is that a walkway carries foot traffic in narrow, concentrated lines, so uneven settling shows up there a lot faster than it does across a wide patio.
                    </p>
                    <p>
                      Every property is a little different. Flat yards, sloped lots, and tree roots nearby. We adjust our prep work based on what we're dealing with. That's part of getting it right the first time.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Year-Round Installation Section - Text Only */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  We Install Paver Walkways <span className="text-gradient-gold">Year-Round</span> in North Jersey
                </h2>
                <div className="space-y-4 text-lg text-muted-foreground">
                  <p>
                    We install walkways throughout the year. Each season has its own advantages, and we adjust our approach based on the conditions. The ground just needs to be workable.
                  </p>
                  <p>
                    Some homeowners in West Orange and Hackensack like to get their walkways done before summer so everything looks sharp for outdoor gatherings. Others book in fall or winter when schedules tend to open up. Either way works.
                  </p>
                  <p>
                    Polymeric sand sets best when temperatures stay between 50 and 80 degrees. If we're working outside that range, we plan around it. Cold weather doesn't stop the project. It just means we time certain steps differently.
                  </p>
                  <p>
                    If you're in Paramus, Clifton, or Wayne and thinking about a new walkway, give us a call or text. We can talk through timing that fits your schedule.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Quality & Longevity Section - Image LEFT, Content RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={pineBrookImage} 
                    alt="Completed paver walkway installation in Pine Brook, NJ showing quality craftsmanship" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Paver Walkways Last <span className="text-gradient-gold">25 to 50 Years</span> with Professional Installation
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      A paver walkway is a long-term investment. When installed correctly, it can last 25 to 50 years. That's the kind of value homeowners in Livingston and Short Hills are looking for.
                    </p>
                    <p>
                      Concrete pavers are built to handle wear. They meet <CitationLink num={2} url="https://www.astm.org">ASTM C936 standards with 8,000 PSI strength</CitationLink>. If one cracks years from now, we can swap it out without tearing up the rest of the walkway. Try doing that with poured concrete.
                    </p>
                    <p>
                      <CitationLink num={5} url="https://www.icpi.org">Pavers interlock, so weight gets distributed across the surface</CitationLink>. Foot traffic won't cause cracking the way it can with a traditional sidewalk.
                    </p>
                    <p>
                      Our winters here in North Jersey are hard on outdoor surfaces. Freeze-thaw cycles can be relentless. Water gets into small gaps, freezes, and expands. That breaks down poorly built walkways over time. Proper base work and drainage can prevent that from happening. <CitationLink num={1} url="https://www.icpi.org">ICPI-certified installers</CitationLink> in Essex and Morris counties build with these conditions in mind.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Curb Appeal Section - Content LEFT, Image RIGHT */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    A New Walkway Adds <span className="text-gradient-gold">Curb Appeal and Value</span> to Your Home
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      A paver walkway does more than look good. It can add real value to your property. <CitationLink num={4} url="https://www.nar.realtor">The National Association of Realtors puts outdoor hardscaping high on the list of features buyers notice</CitationLink>.
                    </p>
                    <p>
                      First impressions matter, and your walkway is one of the first things people see when they visit. A cracked concrete path sends one message, while a clean, well-built paver walkway can send another.
                    </p>
                    <p>
                      In neighborhoods like Glen Ridge and Maplewood, curb appeal goes a long way. Buyers shopping in Bergen and Essex counties often have outdoor features on their checklist. A quality walkway helps your home stand out from the competition.
                    </p>
                    <p>
                      It's also something your family gets to enjoy every day. Walking to the mailbox, coming home from work, and greeting guests at the front door. These little moments add up, and that's value you can see and value you can feel.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img
                    src={palisadesParkImage}
                    alt="New paver walkway installation next to a modern building in Palisades Park, Bergen County, NJ by Masonry Pro Construction"
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Common Mistakes Section - Content LEFT, Image RIGHT */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-6">
                    Common Walkway Installation Mistakes We See <span className="text-gradient-gold">Across North Jersey</span>
                  </h2>
                  <div className="space-y-4 text-lg text-muted-foreground">
                    <p>
                      Contractors who rush through a job tend to cut corners. We get calls from homeowners in Wayne, Paramus, and other towns asking us to fix walkways that weren't done right the first time.
                    </p>
                    <p>
                      <strong>No base underneath</strong> is the biggest issue. Pavers need gravel to stay put. Without it, they sink. We've fixed walkways that were only a couple of seasons old because the base just wasn't there.
                    </p>
                    <p>
                      <strong>Edge restraints</strong> hold everything in place along the border. Skip them, and pavers spread apart over time. Gaps open up, and weeds find their way in.
                    </p>
                    <p>
                      <strong>The sand in the joints matters too.</strong> Regular sand washes out when it rains. Polymeric sand locks the pavers together and keeps weeds from taking root.
                    </p>
                    <p>
                      Here's another thing we talk through before every project: how wide do you want the walkway? You should be able to walk next to someone without stepping off into the grass. We go over width options early so the finished walkway actually fits your property and how you use it.
                    </p>
                  </div>
                </div>
                <div className="aspect-[4/3] rounded-xl overflow-hidden">
                  <img 
                    src={totowaImage} 
                    alt="Paver walkway repair in Totowa, NJ showing common installation mistakes being corrected" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Featured Snippet Section */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  How Long Will a Paver Walkway Last in <span className="text-gradient-gold">North Jersey</span>?
                </h2>
                <p className="text-lg text-muted-foreground mb-6">
                  A professionally installed paver walkway can last 25 to 50 years. In Northern New Jersey, our freeze-thaw cycles can take a toll on outdoor surfaces, so how the walkway is built makes a real difference.
                </p>
                <p className="text-lg text-muted-foreground mb-4">
                  Factors that affect how long your walkway will last:
                </p>
                <ul className="space-y-3 text-lg text-muted-foreground list-disc pl-6">
                  <li>A properly compacted gravel base sized for your soil conditions and foot traffic, per <CitationLink num={1} url="https://www.icpi.org">ICPI standards</CitationLink></li>
                  <li>Polymeric sand in the joints to prevent erosion and weed growth</li>
                  <li>Periodic inspection and resealing every 7 to 15 years, depending on wear and exposure</li>
                </ul>
                <p className="text-lg text-muted-foreground mt-6">
                  When a qualified masonry contractor handles the base prep and drainage, your walkway can hold up for decades in our climate.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ Section - Card Grid */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {faqs.map((faq, index) => (
                  <Card key={index} className="bg-card border-border">
                    <CardHeader>
                      <CardTitle className="text-lg">{faq.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* YouTube Shorts Section */}
          <YouTubeShortsSection 
            title="See Our" 
            highlightedTitle="Work in Action" 
            videoIds={youtubeShortIds}
          />

          <RelatedServices currentCategory="paving" />

          {/* CTA Section with Form */}
          <CTASection 
            heading="Need a" 
            highlightedHeading="Paver Walkway Installation?"
            serviceName="Paver Walkway"
          />

          {/* Citations Accordion */}
          <section className="py-12 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="citations" className="border-border">
                    <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                      Citations and References
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground text-base">
                      <ul className="space-y-3">
                        {citations.map((citation) => (
                          <li key={citation.num} className="text-sm">
                            <sup>{citation.num}</sup>{" "}
                            <a 
                              href={citation.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {citation.text}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          </section>

          {/* Hub Backlink */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-lg text-muted-foreground">
                <p>
                  Walkways are one piece of our <Link to="/paving-services" className="text-secondary hover:underline">North Jersey paving services</Link>, from patios to driveways.
                </p>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
